import {
  FilterResultModel,
  HttpMethod,
  HttpModel,
  IFile,
  PaginationModel,
  convertFiltersToQuery,
} from '@/Shared/domain'
import { axiosFetch } from '@/Shared/infrastructure'
import {
  downloadTransactionSearch,
  downloadTransactionSearchGuarderia,
  getFilterValues,
  getTransactionData,
  getTransactionGuarderia,
  getTransactionLocalsFilter,
} from '@/Shared/infrastructure/endpoints'
import { ProductModel, TransactionModel, TransactionsRepository } from '@/Transactions/domain'
import { TransactionFilterLocals } from '../domain/TransactionFilterLocals'

export function createApiTransactionRepository(): TransactionsRepository {
  return new ApiTransactionRepository()
}

type ApiTransactions = {
  rows: TransactionModel[]
  totalRows: number
}

const convertToDomain = (
  response: ApiTransactions,
): PaginationModel<TransactionModel> => {
  if (!response?.rows) {
    return {
      rows: [],
      totalRows: 0,
    }
  }

  return {
    rows: response.rows,
    totalRows: response.totalRows,
  }
}

export class ApiTransactionRepository implements TransactionsRepository {
  async GetFilterValues(): Promise<HttpModel<ProductModel>> {

    const response = await axiosFetch(
      getFilterValues,
      HttpMethod.GET,
      {}
    )

    if (!response.data) {
      return {
        data: {} as ProductModel,
        meta: response.meta,
      }
    }

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetTransactionData(
    pageNumber: number,
    numberOfRecordsPerPage: number,
    filters?: FilterResultModel[],
  ): Promise<HttpModel<PaginationModel<TransactionModel>>> {
    const merchantList = (filters || [])
      .filter(f => f.name === 'merchantFilterList' || f.name === 'localCode')
      .flatMap(f =>
        f.name === 'localCode'
          ? (f.value || '')
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
          : [f.value],
      )
    const otherFilters = (filters || []).filter(
      f =>
        f.name !== 'merchantFilterList' &&
        f.name !== 'localCode' &&
        f.name !== 'childName' &&
        f.value.trim() !== '',
    )

    const normalizedFilters = otherFilters.reduce<Record<string, string | number>>(
      (acc, filter) => {
        if (filter.name === 'productCode') {
          const parsed = Number(filter.value)
          if (!Number.isNaN(parsed)) {
            acc.productCode = parsed
          }
          return acc
        }

        if (filter.name === 'subProductCode') {
          const rawSubProductCode = filter.value.trim()

          if (rawSubProductCode === '') {
            return acc
          }

          const parsed = Number(rawSubProductCode)

          if (!Number.isNaN(parsed)) {
            acc.subProductCode = parsed
          }
          return acc
        }

        acc[filter.name] = filter.value
        return acc
      },
      {},
    )

    // Cambio realizado: se elimina la query string y se construye bodyData
    // con paginación + filtros para el endpoint SearchV2.
    const bodyData = {
      ...(merchantList.length > 0 ? { merchantFilterList: merchantList } : {}),
      ...normalizedFilters,
      paginationModel: {
        numberOfRecordsPerPage: numberOfRecordsPerPage,
        pageNumber: pageNumber - 1,
        orderByField: '',
        sortDescending: true,
      },
    }

    const response = await axiosFetch(
      getTransactionData,
      HttpMethod.POST,
      bodyData,
    )

    return {
      data: convertToDomain(response.data),
      meta: response.meta,
    }
  }

  async GetTransactionGuarderia(
    pageNumber: number,
    numberOfRecordsPerPage: number,
    filters?: FilterResultModel[],
  ): Promise<HttpModel<PaginationModel<TransactionModel>>> {
    const merchantList = (filters || [])
      .filter(f => f.name === 'merchantFilterList' || f.name === 'localCode')
      .flatMap(f =>
        f.name === 'localCode'
          ? (f.value || '')
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
          : [f.value],
      )
    const otherFilters = (filters || []).filter(
      f => f.name !== 'merchantFilterList' && f.name !== 'localCode',
    )

    const bodyData = {
      ...(merchantList.length > 0 ? { merchantFilterList: merchantList } : {}),
      ...convertFiltersToQuery(otherFilters),
      paginationModel: {
        numberOfRecordsPerPage: numberOfRecordsPerPage,
        pageNumber: pageNumber - 1,
        orderByField: '',
        sortDescending: true,
      },
    }

    const response = await axiosFetch(
      getTransactionGuarderia,
      HttpMethod.POST,
      bodyData,
    )

    return {
      data: convertToDomain(response.data),
      meta: response.meta,
    }
  }

  async DownloadTransactionData(
    filters?: FilterResultModel[],
    isNursery?: boolean,
  ): Promise<HttpModel<IFile>> {
    const merchantList = (filters || [])
      .filter(f => f.name === 'merchantFilterList' || f.name === 'localCode')
      .flatMap(f =>
        f.name === 'localCode'
          ? (f.value || '')
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
          : [f.value],
      )
    const otherFilters = (filters || []).filter(
      f =>
        f.name !== 'merchantFilterList' &&
        f.name !== 'localCode' &&
        (isNursery || f.name !== 'childName') &&
        f.value.trim() !== '',
    )

    // Normaliza filtros numericos para enviar los tipos esperados por el backend.
    const normalizedFilters = otherFilters.reduce<Record<string, string | number>>(
      (acc, filter) => {
        if (filter.name === 'productCode' || filter.name === 'subProductCode') {
          const parsed = Number(filter.value)
          if (!Number.isNaN(parsed)) {
            acc[filter.name] = parsed
          }
          return acc
        }

        acc[filter.name] = filter.value
        return acc
      },
      {},
    )

    const bodyData = {
      ...(merchantList.length > 0 ? { merchantFilterList: merchantList } : {}),
      ...normalizedFilters,
    }

    let response: any
    if (!isNursery) {
      response = await axiosFetch(
        downloadTransactionSearch,
        HttpMethod.POST,
        bodyData,
      )
    } else {
      response = await axiosFetch(
        downloadTransactionSearchGuarderia,
        HttpMethod.POST,
        bodyData,
      )
    }

    return {
      data: response.data ?? null,
      meta: response.meta,
    }
  }

  async GetTransactionLocalsFilter(): Promise<HttpModel<TransactionFilterLocals[]>> {
    const response = await axiosFetch(
      getTransactionLocalsFilter,
      HttpMethod.GET,
      {},
    )

    return {
      data: response.data as TransactionFilterLocals[],
      meta: response.meta,
    }
  }

}
